"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Search, PhoneCall, Compass, Hammer, ChartLine as LineChart, ArrowRight, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
interface ProcessStep {
  step: string;
  title: string;
  subtitle: string;
  description: string;
  deliverables: string;
  highlight: string;
  iconName: "PhoneCall" | "Search" | "Compass" | "Hammer" | "LineChart";
}
const steps: ProcessStep[] = [{
  step: "01",
  title: "Discovery call",
  subtitle: "Understanding your commercial goals",
  description: "I begin with a focused conversation to discuss your commercial priorities, current search challenges, target audience, and business model.",
  deliverables: "Discovery notes, scope definition, and priority roadmap",
  highlight: "Assess fit before making a hiring decision",
  iconName: "PhoneCall"
}, {
  step: "02",
  title: "Comprehensive SEO audit",
  subtitle: "Diagnosing technical bottlenecks & market opportunities",
  description: "I analyse your site architecture, indexing status, keyword coverage, and competitor positions to reveal exactly what holds your organic growth back.",
  deliverables: "Prioritised audit document and technical action checklist",
  highlight: "Manual investigation, zero automated fluff",
  iconName: "Search"
}, {
  step: "03",
  title: "Custom strategy development",
  subtitle: "A tailored commercial search roadmap",
  description: "I formulate a bespoke, practical SEO strategy aligned with your resources. I map high-intent queries, on-page priorities, and authoritative content opportunities.",
  deliverables: "Keyword architecture, content brief framework, and roadmap",
  highlight: "Designed for commercial revenue, not vanity traffic",
  iconName: "Compass"
}, {
  step: "04",
  title: "Hands-on implementation",
  subtitle: "Executing technical, on-page, and structural updates",
  description: "Whether advising your in-house developers or directly modifying page elements and schema, I ensure recommendations are implemented correctly and cleanly.",
  deliverables: "Code specifications, on-page copy optimisations, and testing",
  highlight: "Practical guidance your team can easily deploy",
  iconName: "Hammer"
}, {
  step: "05",
  title: "Reporting & ongoing support",
  subtitle: "Transparent measurement and continuous iteration",
  description: "I monitor indexation, keyword movements, and qualified conversion metrics. Each month you receive clear data and direct strategy reviews to sustain progress.",
  deliverables: "Monthly executive search report and video commentary",
  highlight: "Honest measurement tied to real business impact",
  iconName: "LineChart"
}];
export default function Process() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: "easeOut" as const
      }
    }
  };
  return <section id="consultingprocess" data-nav="light" className="relative bg-background py-20 lg:py-28 overflow-hidden">
      {/* Subtle decorative background glow */}
      <div className="absolute top-1/4 right-0 -z-10 h-96 w-96 rounded-full bg-secondary/30 blur-3xl" />
      <div className="absolute bottom-10 left-0 -z-10 h-80 w-80 rounded-full bg-primary/5 blur-3xl" />

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center mb-16 lg:mb-20">
          <Badge variant="outline" className="mb-4 rounded-full border-primary/20 bg-muted px-4 py-1 text-xs font-semibold text-primary uppercase tracking-wide">
            My Consulting Process
          </Badge>
          <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-foreground mb-5">
            How My SEO Consulting Process Works.
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Every strategic review, audit, and implementation checkpoint follows the same process, wherever your business is based.
          </p>
        </div>

        {/* Timeline List */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="relative">
          {/* Vertical connecting line for desktop */}
          <div className="hidden lg:block absolute left-1/2 top-8 bottom-8 w-px -translate-x-1/2 bg-border" />

          <div className="space-y-8 lg:space-y-12">
            {steps.map((item, i) => {
            const isEven = i % 2 === 1;
            return <motion.div key={item.step} data-index={i} variants={itemVariants} className="relative flex flex-col lg:flex-row items-center gap-6 lg:gap-12">
                  {/* Left Column (Desktop) */}
                  <div className={`w-full lg:w-1/2 flex ${isEven ? "lg:order-2 lg:justify-start" : "lg:order-1 lg:justify-end"}`}>
                    <Card className={`w-full lg:max-w-xl border-border bg-card shadow-sm hover:shadow-md transition-[box-shadow,border-color] duration-300 hover:border-primary/40`}>
                      <CardHeader className="pb-3">
                        <div className="flex items-center justify-between gap-4 mb-2">
                          <span className="font-mono text-sm font-semibold text-primary">
                            Step {item.step}
                          </span>
                          <span className="text-xs font-medium text-muted-foreground bg-muted px-2.5 py-1 rounded-md">
                            {item.highlight}
                          </span>
                        </div>
                        <CardTitle className="text-xl font-semibold text-card-foreground">
                          {item.title}
                        </CardTitle>
                        <p className="text-xs sm:text-sm font-medium text-primary">
                          {item.subtitle}
                        </p>
                      </CardHeader>
                      <CardContent className="space-y-4 text-sm sm:text-base text-muted-foreground leading-relaxed">
                        <p>{item.description}</p>
                        <div className="pt-3 border-t border-border flex items-start gap-2 text-xs sm:text-sm text-foreground">
                          <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                          <span>
                            <strong className="font-medium text-foreground">Deliverables: </strong>
                            {item.deliverables}
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Center Node (Desktop) */}
                  <div className="hidden lg:flex absolute left-1/2 -translate-x-1/2 size-12 rounded-full border-4 border-background bg-primary text-primary-foreground items-center justify-center font-mono text-sm font-bold shadow-md z-10">
                    {item.iconName === "PhoneCall" && <PhoneCall className="size-5" />}
                    {item.iconName === "Search" && <Search className="size-5" />}
                    {item.iconName === "Compass" && <Compass className="size-5" />}
                    {item.iconName === "Hammer" && <Hammer className="size-5" />}
                    {item.iconName === "LineChart" && <LineChart className="size-5" />}
                  </div>

                  {/* Right Column / Placeholder to maintain alternating balance */}
                  <div className={`hidden lg:block w-1/2 ${isEven ? "lg:order-1 text-right pr-6" : "lg:order-2 pl-6"}`}>
                    <div className="text-xs uppercase tracking-widest text-muted-foreground font-medium">
                      Stage {item.step} of 05
                    </div>
                  </div>
                </motion.div>;
          })}
          </div>
        </motion.div>

        {/* Process Milestones / Next Steps Callout */}
        <div className="mt-16 sm:mt-20 rounded-2xl border border-border bg-muted/40 p-6 sm:p-10 text-center max-w-4xl mx-auto shadow-xs">
          <div className="inline-flex items-center gap-2 text-xs font-semibold text-primary uppercase tracking-wider mb-3">
            <span className="size-2 rounded-full bg-primary" />
            Engagement Process & Milestones
          </div>
          <h3 className="text-xl sm:text-2xl font-semibold text-foreground mb-3">
            Clear milestones and transparent deliverables at every stage
          </h3>
          <p className="text-sm sm:text-base text-muted-foreground max-w-2xl mx-auto mb-8">
            From the initial discovery call through to audit delivery, implementation, and monthly reporting, each phase follows an agreed schedule with documented deliverables. You always know what is being worked on, what comes next, and how progress is measured.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button size="lg" asChild className="w-full sm:w-auto font-medium">
              <Link to="/ContactPage">
                Book a Free SEO Consultation
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
            <Button variant="outline" size="lg" asChild className="w-full sm:w-auto font-medium border-border hover:bg-background">
              <Link to="/SeoAuditsPage">Get a Free SEO Audit</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}